import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadFile, errMsg, useApi } from "@/lib/api";

// Self-service export (owner/manager): static copy of the published site + data files. Also available while the space is suspended.
export default function AreaExport({ tid }) {
  const api = useApi();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get(`/t/${tid}/export`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, [api, tid]);
  const get = async (key, url, name) => {
    setBusy(key);
    try { await downloadFile(api, url, name); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const Btn = ({ k, url, name, children }) => (
    <Button variant="outline" className="h-11 justify-start gap-2 sm:h-10" disabled={!!busy || d?.closed} onClick={() => get(k, url, name)} data-testid={`export-${k}`}>
      <Download size={16} aria-hidden="true" />{busy === k ? "Preparazione…" : children}
    </Button>);
  if (!d) return <p className="text-sm text-slate-500" data-testid="export-loading">Caricamento…</p>;
  return (
    <section className="space-y-6" data-testid="area-export">
      {d.closed && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="export-closed">Il periodo per esportare i dati è terminato: contatta l'assistenza.</p>}
      <div className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900">Copia statica del sito</h2>
        <p className="text-sm text-slate-600" data-testid="export-site-note">Pagine, stili e immagini dell'ultima versione pubblicata, da caricare su un altro hosting. Moduli, prenotazioni, calendari, ordini, carrello, CRM e automazioni non sono inclusi: al loro posto compare un avviso con i recapiti pubblici dell’attività, se indicati.</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {d.sites.filter((s) => s.published).map((s) => <Btn key={s.id} k={`site-${s.id}`} url={`/t/${tid}/export/site/${s.id}.zip`} name="sito.zip">Sito «{s.name}» (ZIP)</Btn>)}
          {!d.sites.some((s) => s.published) && <p className="text-sm text-slate-500" data-testid="export-no-site">Nessun sito pubblicato da esportare.</p>}
        </div>
      </div>
      <div className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900">I tuoi dati</h2>
        <p className="text-sm text-slate-600">File separati dal sito, in formato CSV.</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Btn k="contacts" url={`/t/${tid}/crm/sheet/export?format=csv`} name="contatti.csv">Contatti</Btn>
          <Btn k="bookings" url={`/t/${tid}/export/bookings.csv`} name="prenotazioni.csv">Prenotazioni</Btn>
          <Btn k="orders" url={`/t/${tid}/export/orders.csv`} name="ordini.csv">Ordini</Btn>
        </div>
      </div>
    </section>);
}
